import { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiImage, FiX } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import './LensPickerModal.css';

/** A lens the picker hands back: what the Home Layout page keeps per pick. */
export interface PickedLens {
  id: string;
  name: string;
  imageUrl?: string;
}

/** One row of GET /api/admin/extensions, as far as the picker reads it. */
interface LensRow {
  _id: string;
  Name: string;
  creatorName?: string;
  Creator?: string;
  thumbnailImageUrl?: string;
  aiModel?: string;
  isPublic?: boolean;
  isPublished?: boolean;
  isArchived?: boolean;
  reportStatus?: string;
}

interface ModelOption {
  model_id: string;
  display_name?: string;
}

interface LensPickerModalProps {
  open: boolean;
  /** Lenses already in the list: shown as added, not selectable again. */
  pickedIds: string[];
  /** How many more lenses the list can take. */
  remaining: number;
  /** Called with the selected lenses, in the order they were clicked. */
  onAdd: (lenses: PickedLens[]) => void;
  onClose: () => void;
}

const PAGE_SIZE = 30;

/**
 * Why the app would skip this lens, judged on the lens alone (its creator's
 * plan is not known here). Undefined = nothing on the lens itself hides it.
 */
const hiddenReason = (lens: LensRow): string | undefined => {
  if (lens.reportStatus === 'Suspended') return 'Suspended';
  if (lens.isArchived) return 'Archived';
  if (lens.isPublished === false) return 'Draft';
  if (!lens.isPublic) return 'Private';
  return undefined;
};

/**
 * Grid picker for a Lenses List element: filter the admin lens list by lens
 * name, creator and model, select several lenses, add them in one go.
 */
export default function LensPickerModal({ open, pickedIds, remaining, onAdd, onClose }: LensPickerModalProps) {
  const [name, setName] = useState('');
  const [creator, setCreator] = useState('');
  const [model, setModel] = useState('');
  const [onlyShowable, setOnlyShowable] = useState(true);
  const [models, setModels] = useState<ModelOption[]>([]);
  const [rows, setRows] = useState<LensRow[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Insertion order is the order the lenses are added to the list
  const [selected, setSelected] = useState<Map<string, PickedLens>>(new Map());
  // Drops responses that a newer filter change has overtaken
  const requestSeq = useRef(0);

  useEscapeToClose(open, onClose);

  const picked = useMemo(() => new Set(pickedIds), [pickedIds]);

  // Fresh selection every time the picker opens; filters are kept between opens
  useEffect(() => {
    if (open) setSelected(new Map());
  }, [open]);

  // Model filter options, loaded once
  useEffect(() => {
    if (!open || models.length > 0) return;
    apiRequest<{ success: boolean; data: ModelOption[] } | ModelOption[]>('/api/admin/image-models')
      .then((res) => {
        const list = Array.isArray(res) ? res : res.success ? res.data : [];
        setModels(list || []);
      })
      .catch((err) => console.error('Error loading image models for lens picker:', err));
  }, [open, models.length]);

  const load = async (pageToLoad: number, replace: boolean) => {
    const seq = ++requestSeq.current;
    const params = new URLSearchParams({
      page: String(pageToLoad),
      limit: String(PAGE_SIZE),
      isDeleted: 'false',
      ...(name.trim() && { name: name.trim() }),
      ...(creator.trim() && { creatorQuery: creator.trim() }),
      ...(model && { aiModel: model }),
      // Not filtered on reportStatus: lenses older than that field have none
      // stored, and a filter would drop them. A suspended lens is flagged instead.
      ...(onlyShowable && { isPublic: 'true', isPublished: 'true', isArchived: 'false' }),
    });
    try {
      setLoading(true);
      setError(null);
      const res = await apiRequest<{ success: boolean; data: LensRow[]; pagination: { page: number; pages: number }; error?: string }>(
        `/api/admin/extensions?${params}`
      );
      if (seq !== requestSeq.current) return;
      if (!res.success) {
        setError(res.error || 'Failed to load lenses');
        return;
      }
      setRows((prev) => (replace ? res.data : [...prev, ...res.data]));
      setPage(pageToLoad);
      setHasMore(res.pagination.page < res.pagination.pages);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load lenses');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  // Debounced reload from page 1 whenever a filter changes
  useEffect(() => {
    if (!open) return;
    const debounce = setTimeout(() => load(1, true), 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, name, creator, model, onlyShowable]);

  if (!open) return null;

  const modelLabel = (modelId?: string) => {
    if (!modelId) return '';
    return models.find((m) => m.model_id === modelId)?.display_name || modelId;
  };

  const toggle = (lens: LensRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(lens._id)) {
        next.delete(lens._id);
      } else if (next.size < remaining) {
        next.set(lens._id, { id: lens._id, name: lens.Name, imageUrl: lens.thumbnailImageUrl });
      }
      return next;
    });
  };

  const selectedOrder = [...selected.keys()];
  const atCapacity = selected.size >= remaining;

  return (
    // No backdrop-click close, like the page's other modals: a stray click would lose the selection
    <div className="hl-modal-overlay lp-overlay">
      <div className="hl-modal lp-modal">
        <h2 className="hl-modal-title lp-title">
          Add lenses
          <button type="button" className="lp-close" title="Close" onClick={onClose}><FiX size={18} /></button>
        </h2>

        <div className="lp-filters">
          <input
            type="text"
            className="hl-input"
            placeholder="Lens name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <input
            type="text"
            className="hl-input"
            placeholder="Creator (name, username, FRAW ID, email)"
            value={creator}
            onChange={(e) => setCreator(e.target.value)}
          />
          <select className="hl-input" value={model} onChange={(e) => setModel(e.target.value)}>
            <option value="">All models</option>
            {models.map((m) => (
              <option key={m.model_id} value={m.model_id}>{m.display_name || m.model_id}</option>
            ))}
          </select>
          <label className="lp-toggle" title="Public, published and not archived. A suspended lens still appears, flagged.">
            <input type="checkbox" checked={onlyShowable} onChange={(e) => setOnlyShowable(e.target.checked)} />
            Only lenses the app can show
          </label>
        </div>

        <div className="hl-modal-body lp-body">
          {error && <div className="lp-message lp-error">{error}</div>}
          {!error && !loading && rows.length === 0 && <div className="lp-message">No lenses match these filters.</div>}

          <div className="lp-grid">
            {rows.map((lens) => {
              const isPicked = picked.has(lens._id);
              const isSelected = selected.has(lens._id);
              const disabled = isPicked || (!isSelected && atCapacity);
              const reason = hiddenReason(lens);
              return (
                <button
                  key={lens._id}
                  type="button"
                  className={`lp-card${isSelected ? ' selected' : ''}${isPicked ? ' picked' : ''}`}
                  disabled={disabled}
                  title={isPicked ? 'Already in the list' : lens.Name}
                  onClick={() => toggle(lens)}
                >
                  <div className="lp-card-media">
                    {lens.thumbnailImageUrl
                      ? <img src={lens.thumbnailImageUrl} alt="" loading="lazy" />
                      : <span className="lp-card-empty"><FiImage size={22} /></span>}
                    {isSelected && <span className="lp-card-check">{selectedOrder.indexOf(lens._id) + 1}</span>}
                    {isPicked && <span className="lp-card-flag added"><FiCheck size={11} /> Added</span>}
                    {!isPicked && reason && <span className="lp-card-flag hidden">{reason}</span>}
                  </div>
                  <span className="lp-card-name">{lens.Name}</span>
                  <span className="lp-card-meta">by {lens.creatorName || lens.Creator || 'Unknown'}</span>
                  {lens.aiModel && <span className="lp-card-meta lp-card-model">{modelLabel(lens.aiModel)}</span>}
                </button>
              );
            })}
          </div>

          {loading && <div className="lp-message">Loading…</div>}
          {!loading && hasMore && (
            <button type="button" className="hl-cancel-btn lp-more" onClick={() => load(page + 1, false)}>
              Load more
            </button>
          )}
        </div>

        <div className="hl-modal-actions lp-actions">
          <span className="lp-count">
            {selected.size} selected
            {atCapacity && remaining > 0 && ' — the list is full'}
          </span>
          <button type="button" className="hl-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="hl-confirm-btn"
            disabled={selected.size === 0}
            onClick={() => onAdd([...selected.values()])}
          >
            Add {selected.size || ''} {selected.size === 1 ? 'lens' : 'lenses'}
          </button>
        </div>
      </div>
    </div>
  );
}
